import { ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { debounce, DEBOUNCE_DELAY_DEFAULT } from '@/utilities/debounce';
import { submissionTracker } from '@/utilities/submissionTracking';

export interface UseDebounceSubmitOptions {
  /**
   * Unique key for tracking submissions (e.g., 'request-123', 'refuel-456')
   */
  submissionKey?: string;
  /**
   * Debounce delay in milliseconds
   */
  debounceDelay?: number;
  /**
   * Whether to track duplicate submissions
   */
  trackDuplicates?: boolean;
  /**
   * Custom error message for duplicate submissions
   */
  duplicateMessage?: string;
  /**
   * Success message to show after submission
   */
  successMessage?: string;
  /**
   * Error message to show on failure
   */
  errorMessage?: string;
}

export interface UseDebounceSubmitReturn<T> {
  /**
   * Whether a submission is currently in progress
   */
  isSubmitting: Ref<boolean>;
  /**
   * The debounced submit function
   */
  submit: (data?: T) => void;
  /**
   * Reset the submission state
   */
  reset: () => void;
  /**
   * Any error that occurred during submission
   */
  error: Ref<Error | null>;
}

/**
 * Composable for handling debounced form submissions with duplicate prevention
 * 
 * @param submitFn The async function to call when submitting
 * @param options Configuration options
 * @returns Object with isSubmitting state and submit function
 */
export function useDebounceSubmit<T = any>(
  submitFn: (data?: T) => Promise<any>,
  options: UseDebounceSubmitOptions = {}
): UseDebounceSubmitReturn<T> {
  const {
    submissionKey,
    debounceDelay = DEBOUNCE_DELAY_DEFAULT,
    trackDuplicates = true,
    duplicateMessage = 'This form was just submitted. Please wait a moment before submitting again.',
    successMessage,
    errorMessage = 'An error occurred during submission.',
  } = options;

  const toast = useToast();
  const isSubmitting = ref(false);
  const error = ref<Error | null>(null);

  // Create the debounced submit logic
  const debouncedSubmitLogic = debounce(async (data?: T) => {
    // Don't proceed if already submitting
    if (isSubmitting.value) return;

    // Check for duplicate submission if enabled
    if (trackDuplicates && submissionKey && data) {
      if (submissionTracker.isDuplicateSubmission(submissionKey, data)) {
        toast.add({
          severity: 'warn',
          summary: 'Duplicate Submission',
          detail: duplicateMessage,
          life: 3000
        });
        return;
      }
    }

    isSubmitting.value = true;
    error.value = null;

    try {
      const result = await submitFn(data);

      // Show success message if provided
      if (successMessage) {
        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: successMessage,
          life: 3000
        });
      }

      // Clear submission tracking on success
      if (submissionKey) {
        submissionTracker.clearSubmission(submissionKey);
      }

      return result;
    } catch (err) {
      error.value = err as Error;
      
      // Show error message
      const detail = err instanceof Error && err.message ? err.message : errorMessage;
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail,
        life: 5000
      });

      throw err;
    } finally {
      // Reset submitting state after a brief delay
      setTimeout(() => {
        isSubmitting.value = false;
      }, 500);
    }
  }, debounceDelay);

  // Wrapper function that calls the debounced submit
  const submit = (data?: T) => {
    debouncedSubmitLogic(data);
  };

  // Reset function to clear state
  const reset = () => {
    isSubmitting.value = false;
    error.value = null;
    if (submissionKey) {
      submissionTracker.clearSubmission(submissionKey);
    }
  };

  return {
    isSubmitting,
    submit,
    reset,
    error
  };
}